import React, { useState } from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Input from "@amzn/meridian/input";
import TimePicker from "@amzn/meridian/time-picker";
import Select, { SelectOption } from "@amzn/meridian/select";
import {
  SSD_DELIVERY_WINDOW,
  SSD_DEPARTMENT,
  SSD_ROLE,
  SSD_ROLE_NAME,
  SSD_VOLUME_SOURCE_TYPE,
} from "../SettingsConstant";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import minusCircleTokens from "@amzn/meridian-tokens/base/icon/minus-circle";
import { ModalFooter } from "@amzn/meridian/modal";
import { isValidTimeFormat } from "../../SettingsUtil";
import { TIME_PATTERN } from "../../../util/Constants";
import { chainWalk, isValidNumber } from "../../../util/Util";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";

const DEFAULT_VOLUME_SOURCE = {
  source_type: SSD_VOLUME_SOURCE_TYPE.ROLE,
  department: "-",
  source_key: "-",
  delay_in_seconds: 0,
  conversion: 1,
};

const UpsertRateRule = ({ rules, rule, onSubmit, onCancel, isEditRule }) => {
  const initialUpdatedRule = JSON.parse(JSON.stringify(rule));
  if (!initialUpdatedRule["labor_need"]) {
    initialUpdatedRule["labor_need"] = [{}];
  }
  if (
    !initialUpdatedRule["volumes_to_process"] ||
    initialUpdatedRule["volumes_to_process"].filter(
      ({ source_type }) => source_type === SSD_VOLUME_SOURCE_TYPE.ROLE,
    ).length <= 0
  ) {
    initialUpdatedRule["volumes_to_process"] = [
      ...chainWalk(() => initialUpdatedRule["volumes_to_process"], []),
      { ...DEFAULT_VOLUME_SOURCE },
    ];
  }
  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const [updatedRule, setUpdatedRule] = useState(initialUpdatedRule);
  const availableRoleNames = Object.values(SSD_ROLE_NAME).filter(
    (roleName) => rules.filter(({ name }) => name === roleName).length === 0,
  );
  if (updatedRule.name && !availableRoleNames.includes(updatedRule.name)) {
    availableRoleNames.push(updatedRule.name);
  }
  const onChangeRole = (roleName) => {
    updatedRule.name = roleName;
    updatedRule.label = SSD_ROLE[roleName].roleLabel;
    updatedRule.department_name = SSD_ROLE[roleName].departmentName;
    updatedRule.process = SSD_ROLE[roleName].processName;
    updatedRule.businessType = SSD_ROLE[roleName].businessType;
    updatedRule.speed = SSD_ROLE[roleName].speed;
    updatedRule.direct_role = SSD_ROLE[roleName].isDirectRole;
    updatedRule.volume_type = SSD_ROLE[roleName].volumeType;
    updatedRule.order = SSD_ROLE[roleName].order;
    updatedRule.volumes_to_process = [
      ...SSD_ROLE[roleName].volumesToProcess.map(
        ({ sourceType, sourceKey, delayInSeconds, conversion }) => ({
          source_type: sourceType,
          source_key: sourceKey,
          delay_in_seconds: delayInSeconds,
          conversion,
        }),
      ),
      ...updatedRule.volumes_to_process.filter(
        ({ source_type }) => source_type === SSD_VOLUME_SOURCE_TYPE.ROLE,
      ),
    ];
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMinHeadcount = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["min_hc"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMaxHeadcount = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["max_hc"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMaxTph = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["max_throughput"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMaxBacklog = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["max_backlog"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMinRate = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["min_rate"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeMaxRate = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["max_rate"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeDefaultRate = (val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    updatedRule["default_rate"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeDeliveryWindow = (laborNeedInterval, val) => {
    laborNeedInterval["delivery_window"] = val;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeEarliestStart = (laborNeedInterval, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      laborNeedInterval["earliest_start_time_local"] = val.slice(0, -3);
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onChangeTargetStart = (laborNeedInterval, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      laborNeedInterval["target_start_time_local"] = val.slice(0, -3);
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onChangeTargetDeadline = (laborNeedInterval, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      laborNeedInterval["target_deadline_time_local"] = val.slice(0, -3);
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onChangeFinalDeadline = (laborNeedInterval, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      laborNeedInterval["final_deadline_time_local"] = val.slice(0, -3);
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onDeleteLaborNeedInterval = (laborNeedIntervalToDelete) => {
    updatedRule["labor_need"] = updatedRule["labor_need"].filter(
      (laborNeedInterval) => laborNeedInterval !== laborNeedIntervalToDelete,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onAddLaborNeedInterval = () => {
    updatedRule["labor_need"] = [...updatedRule["labor_need"], {}];
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeUpstreamVolumeSourceDepartment = (
    volumeSource,
    departmentName,
  ) => {
    const roles = Object.values(SSD_ROLE).filter(
      (role) => role.departmentName === departmentName,
    );
    volumeSource["department"] = departmentName;
    volumeSource["source_key"] =
      departmentName === "-" ? "-" : chainWalk(() => roles[0].roleName, "");
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeUpstreamVolumeSourceRole = (volumeSource, roleName) => {
    volumeSource["source_key"] = roleName;
    setUpdatedRule({ ...updatedRule });
  };

  const onDeleteVolumeSource = (volumeSourceToDelete) => {
    updatedRule["volumes_to_process"] = updatedRule[
      "volumes_to_process"
    ].filter((volumeSource) => volumeSource !== volumeSourceToDelete);
    setUpdatedRule({ ...updatedRule });
  };

  const onAddVolumeSource = () => {
    updatedRule["volumes_to_process"] = [
      ...updatedRule["volumes_to_process"],
      { ...DEFAULT_VOLUME_SOURCE },
    ];
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeDelayInSeconds = (volumeSource, val) => {
    if (!isValidNumber(val) || val < 0) {
      volumeSource["delay_in_seconds"] = 0;
    } else {
      volumeSource["delay_in_seconds"] = parseInt(val, 10);
    }
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeConversion = (volumeSource, val) => {
    if (!isValidNumber(val) || val < 0) {
      volumeSource["conversion"] = 0;
    } else {
      volumeSource["conversion"] = parseInt(val, 10);
    }
    setUpdatedRule({ ...updatedRule });
  };

  const onClickSubmit = () => {
    if (!updatedRule.name) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select a role.`,
        }),
      );
    }
    let {
      min_hc,
      max_hc,
      min_rate,
      max_rate,
      default_rate,
      max_backlog,
      max_throughput,
    } = updatedRule;
    if (!isValidNumber(min_hc) || min_hc < 0) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a min headcount.`,
        }),
      );
    } else {
      min_hc = parseInt(min_hc, 10);
    }
    if (!isValidNumber(max_hc) || max_hc < 0) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a max headcount.`,
        }),
      );
    } else {
      max_hc = parseInt(max_hc, 10);
    }
    if (!isValidNumber(min_rate) || min_rate < 0) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a min rate.`,
        }),
      );
    } else {
      min_rate = parseInt(min_rate, 10);
    }
    if (!isValidNumber(max_rate) || max_rate < 0) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a max rate.`,
        }),
      );
    } else {
      max_rate = parseInt(max_rate, 10);
    }
    if (!isValidNumber(default_rate) || default_rate < 0) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a default rate.`,
        }),
      );
    } else {
      default_rate = parseInt(default_rate, 10);
    }
    if (!isValidNumber(max_backlog) || max_backlog < 0) {
      max_backlog = null;
    } else {
      max_backlog = parseInt(max_backlog, 10);
    }
    if (!isValidNumber(max_throughput) || max_throughput < 0) {
      max_throughput = null;
    } else {
      max_throughput = parseInt(max_throughput, 10);
    }
    const volumes_to_process = updatedRule.volumes_to_process.filter(
      ({ source_type, source_key, department }) =>
        !(
          source_type === SSD_VOLUME_SOURCE_TYPE.ROLE &&
          source_key === "-" &&
          department === "-"
        ),
    );
    if (
      updatedRule.labor_need.filter(
        ({
          delivery_window,
          earliest_start_time_local,
          target_start_time_local,
          target_deadline_time_local,
          final_deadline_time_local,
        }) =>
          !delivery_window ||
          !earliest_start_time_local ||
          !target_start_time_local ||
          !target_deadline_time_local ||
          !final_deadline_time_local,
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter the missing delivery window, start time, and deadline for the labor needed intervals.`,
        }),
      );
    }
    onSubmit({
      ...updatedRule,
      min_hc,
      max_hc,
      min_rate,
      max_rate,
      default_rate,
      max_backlog,
      max_throughput,
      volumes_to_process,
      labor_need: updatedRule.labor_need,
    });
    onCancel();
  };

  return (
    <Column>
      <React.Fragment>
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {toast.message}
            </Alert>
          )}
        </Toaster>
      </React.Fragment>
      <Row alignmentHorizontal="center">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Role *
          </Text>
          <Select
            value={updatedRule.name}
            onChange={onChangeRole}
            placeholder="Select a role"
            width={200}
            disabled={isEditRule}
          >
            {availableRoleNames.map((roleName) => (
              <SelectOption
                value={roleName}
                label={SSD_ROLE[roleName].roleLabel}
              />
            ))}
          </Select>
        </Column>
        <Column>
          <Row height={10} />
          <Text>requires</Text>
        </Column>
      </Row>

      <div
        style={{ width: "100%", height: 1, backgroundColor: "rgba(0,0,0,0.1)" }}
      />
      <Row spacing="200">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Min. Headcount *
          </Text>
          <Input
            value={updatedRule["min_hc"]}
            onChange={onChangeMinHeadcount}
            type="number"
            width={200}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Max. Headcount *
          </Text>
          <Input
            value={updatedRule["max_hc"]}
            onChange={onChangeMaxHeadcount}
            type="number"
            width={200}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Max. TPH
          </Text>
          <Input
            value={updatedRule["max_throughput"]}
            onChange={onChangeMaxTph}
            type="number"
            width={200}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Max. Backlog
          </Text>
          <Input
            value={updatedRule["max_backlog"]}
            onChange={onChangeMaxBacklog}
            type="number"
            width={200}
          />
        </Column>
      </Row>
      <Text type="h300">Rates</Text>
      <Row spacing="200">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Minimum *
          </Text>
          <Input
            value={updatedRule["min_rate"]}
            onChange={onChangeMinRate}
            type="number"
            width={200}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Maximum *
          </Text>
          <Input
            value={updatedRule["max_rate"]}
            onChange={onChangeMaxRate}
            type="number"
            width={200}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Default *
          </Text>
          <Input
            value={updatedRule["default_rate"]}
            onChange={onChangeDefaultRate}
            type="number"
            width={200}
          />
        </Column>
      </Row>
      <Text type="h300">Upstream Role</Text>
      {updatedRule["volumes_to_process"]
        .filter(
          ({ source_type }) => source_type === SSD_VOLUME_SOURCE_TYPE.ROLE,
        )
        .map((volumeSource, i, volumeSources) => (
          <Row spacing="200">
            <Column>
              <Text tag="label" type="b200" color="primary">
                Department
              </Text>
              <Select
                value={volumeSource.department}
                onChange={(val) =>
                  onChangeUpstreamVolumeSourceDepartment(volumeSource, val)
                }
                placeholder="Select a department"
                width={200}
              >
                <SelectOption value="-" label="-" />
                {Object.entries(SSD_DEPARTMENT).map((entry) => (
                  <SelectOption
                    value={entry[1].departmentName}
                    label={entry[1].departmentLabel}
                  />
                ))}
              </Select>
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                Role
              </Text>
              <Select
                value={volumeSource["source_key"]}
                onChange={(val) =>
                  onChangeUpstreamVolumeSourceRole(volumeSource, val)
                }
                placeholder="Select a role"
                width={200}
              >
                {volumeSource.department === "-" && (
                  <SelectOption label={"-"} value={"-"} />
                )}
                {Object.entries(SSD_ROLE)
                  .filter(
                    (entry) =>
                      entry[1].departmentName === volumeSource.department,
                  )
                  .map((entry) => (
                    <SelectOption
                      value={entry[1].roleName}
                      label={entry[1].roleLabel}
                    />
                  ))}
              </Select>
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                Delay in seconds
              </Text>
              <Input
                value={
                  volumeSource["department"] === "-"
                    ? " "
                    : volumeSource["delay_in_seconds"]
                }
                onChange={(val) => onChangeDelayInSeconds(volumeSource, val)}
                type="number"
                width={200}
              />
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                Conversion
              </Text>
              <Input
                value={
                  volumeSource["department"] === "-"
                    ? " "
                    : volumeSource["conversion"]
                }
                onChange={(val) => onChangeConversion(volumeSource, val)}
                type="number"
                width={200}
              />
            </Column>
            <Column>
              <Row height={15} />
              <Row>
                {volumeSources.length > 1 && (
                  <Button
                    type="icon"
                    onClick={() => onDeleteVolumeSource(volumeSource)}
                  >
                    <Text color="error">
                      <Icon tokens={minusCircleTokens} />
                    </Text>
                  </Button>
                )}
                {i === volumeSources.length - 1 && (
                  <Button type="icon" onClick={onAddVolumeSource}>
                    <div style={{ color: "#63a63a" }}>
                      <Icon tokens={plusCircleTokens} />
                    </div>
                  </Button>
                )}
              </Row>
            </Column>
          </Row>
        ))}
      <Text type="h300">Labor Need</Text>
      {updatedRule["labor_need"].map((laborNeedInterval, i) => (
        <Row spacing="200">
          <Column>
            <Text tag="label" type="b200" color="primary">
              Delivery Window *
            </Text>
            <Select
              value={laborNeedInterval["delivery_window"]}
              onChange={(val) => onChangeDeliveryWindow(laborNeedInterval, val)}
              placeholder="Select a delivery window"
              width={200}
            >
              <SelectOption
                value={SSD_DELIVERY_WINDOW.BREAKFAST}
                label={SSD_DELIVERY_WINDOW.BREAKFAST}
              />
              <SelectOption
                value={SSD_DELIVERY_WINDOW.BRUNCH}
                label={SSD_DELIVERY_WINDOW.BRUNCH}
              />
              <SelectOption
                value={SSD_DELIVERY_WINDOW.LUNCH}
                label={SSD_DELIVERY_WINDOW.LUNCH}
              />
              <SelectOption
                value={SSD_DELIVERY_WINDOW.DINNER}
                label={SSD_DELIVERY_WINDOW.DINNER}
              />
              <SelectOption
                value={SSD_DELIVERY_WINDOW.BEDTIME}
                label={SSD_DELIVERY_WINDOW.BEDTIME}
              />
            </Select>
          </Column>
          <Column>
            <Text tag="label" type="b200" color="primary">
              Earliest Start *
            </Text>
            <TimePicker
              placeholder="MM:HH"
              value={laborNeedInterval["earliest_start_time_local"] + ":00"}
              onChange={(val) => onChangeEarliestStart(laborNeedInterval, val)}
              width={150}
            />
          </Column>
          <Column>
            <Text tag="label" type="b200" color="primary">
              Target Start *
            </Text>
            <TimePicker
              placeholder="MM:HH"
              value={laborNeedInterval["target_start_time_local"] + ":00"}
              onChange={(val) => onChangeTargetStart(laborNeedInterval, val)}
              width={150}
            />
          </Column>
          <Column>
            <Text tag="label" type="b200" color="primary">
              Target Deadline *
            </Text>
            <TimePicker
              placeholder="MM:HH"
              value={laborNeedInterval["target_deadline_time_local"] + ":00"}
              onChange={(val) => onChangeTargetDeadline(laborNeedInterval, val)}
              width={150}
            />
          </Column>
          <Column>
            <Text tag="label" type="b200" color="primary">
              Final Deadline *
            </Text>
            <TimePicker
              placeholder="MM:HH"
              value={laborNeedInterval["final_deadline_time_local"] + ":00"}
              onChange={(val) => onChangeFinalDeadline(laborNeedInterval, val)}
              width={150}
            />
          </Column>
          <Column>
            <Row height={15} />
            <Row>
              {updatedRule["labor_need"].length > 1 && (
                <Button
                  type="icon"
                  onClick={() => onDeleteLaborNeedInterval(laborNeedInterval)}
                >
                  <Text color="error">
                    <Icon tokens={minusCircleTokens} />
                  </Text>
                </Button>
              )}
              {i === updatedRule["labor_need"].length - 1 && (
                <Button type="icon" onClick={onAddLaborNeedInterval}>
                  <div style={{ color: "#63a63a" }}>
                    <Icon tokens={plusCircleTokens} />
                  </div>
                </Button>
              )}
            </Row>
          </Column>
        </Row>
      ))}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onCancel}>
            Close
          </Button>
          <Button type="primary" size="small" onClick={onClickSubmit}>
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Column>
  );
};
export default UpsertRateRule;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/UpsertRateRule.js